import React, { useMemo, useState } from 'react';
import ItineraryDay from './ItineraryDay';
import ItineraryTableView from './ItineraryTableView';
import ActivityTitle from '@/components/common/ActivityTitle';
import Border from '@/components/common/border';
import { ItineraryDayProps, PackageTransfer } from '@/types/itineraryTypes';
import { BackendCurrency } from '@/utils/currencyHelpers';
import type { Hotel } from '@/components/packages/Hotels';
import type { Vehicle } from '@/components/packages/Vehicles';
import type { TransferDisplayItem } from './ItineraryInfoPopover';
import {
  formatRoomOptionsLabel,
  NormalizedRoomOption,
  parseRoomOptionsInput,
} from '@/utils/roomOptionHelpers';

interface ItineraryProps {
  itineraries: ItineraryDayProps[];
  backendCurrency?: BackendCurrency;
  hotels?: Hotel[];
  vehicles?: Vehicle[];
  transfers?: PackageTransfer[];
  airports?: Array<{ _id: string; name?: string }>;
  /** When false, hide the (i) info icon next to each day. When true/undefined, show it if hotels/vehicles exist. */
  showItineraryInfoIcon?: boolean;
  /** When false, hide benefits section in each day. */
  showBenefits?: boolean;
  /** When true, show a hotel section using highlight cards. */
  showHotelAsHighlights?: boolean;
  /** Optional package start date (ISO string or Date) for grid view date column */
  startDate?: string | Date;
  /** True when this itinerary is rendered in booking flow */
  isBookingContext?: boolean;
}

function parsePoint(point: string): { type: 'airport' | 'hotel'; id: string } {
  const [type, id] = point.split(':');
  return {
    type: type === 'airport' ? 'airport' : 'hotel',
    id: id?.trim() ?? '',
  };
}

function resolvePointName(
  point: string,
  hotels: (Hotel & { _id?: string; uid?: string })[],
  airports: Array<{ _id: string; name?: string }>
): string {
  const { type, id } = parsePoint(point);
  if (!id) return '—';
  if (type === 'hotel') {
    const h = hotels.find(h => h._id === id || h.uid === id);
    return h?.name ?? id;
  }
  const a = airports.find(a => a._id === id);
  return a?.name ?? id;
}

type ViewMode = 'list' | 'grid';

const Itinerary: React.FC<ItineraryProps> = ({
  itineraries,
  backendCurrency,
  hotels = [],
  vehicles = [],
  transfers = [],
  airports = [],
  showItineraryInfoIcon = true,
  showBenefits = true,
  showHotelAsHighlights = false,
  startDate: startDateProp,
  isBookingContext = false,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const startDate = startDateProp
    ? typeof startDateProp === 'string'
      ? new Date(startDateProp)
      : startDateProp
    : null;

  // Sort itineraries by sortOrder field to match admin panel order
  const sortedItineraries = [...itineraries].sort((a, b) => {
    const orderA = a.sortOrder ?? 0;
    const orderB = b.sortOrder ?? 0;
    return orderA - orderB;
  });

  // Map transfer to day index: before[i] = transfer into day i, after[i] = transfer out of day i
  const transferByDay = useMemo(() => {
    const before: (PackageTransfer | null)[] = sortedItineraries.map(
      () => null
    );
    const after: (PackageTransfer | null)[] = sortedItineraries.map(() => null);
    for (const transfer of transfers) {
      if (!transfer.pickUpPoint || !transfer.dropOffPoint) continue;
      const pickUp = parsePoint(transfer.pickUpPoint);
      const dropOff = parsePoint(transfer.dropOffPoint);

      if (pickUp.type === 'airport' && dropOff.type === 'hotel') {
        before[0] = transfer;
      } else if (pickUp.type === 'hotel' && dropOff.type === 'hotel') {
        const idx = sortedItineraries.findIndex(
          it =>
            (it as ItineraryDayProps & { hotelId?: { _id: string } }).hotelId
              ?._id === pickUp.id
        );
        if (idx !== -1) after[idx] = transfer;
      }
    }
    return { before, after };
  }, [sortedItineraries, transfers]);

  // Resolve vehicleId to Vehicle
  const vehicleById = useMemo(() => {
    const map: Record<string, Vehicle> = {};
    vehicles.forEach(v => {
      if (v._id) map[v._id] = v;
    });
    return map;
  }, [vehicles]);

  // Hotel may have _id or uid from API for matching itinerary hotelId
  type HotelWithId = Hotel & { _id?: string; uid?: string };

  // Per-day: hotel/room/transfer from itinerary item when populated, else resolve from package-level lists
  const dayInfo = useMemo(() => {
    return sortedItineraries.map((itinerary, index) => {
      const it = itinerary as ItineraryDayProps & {
        hotelId?: { _id: string; name?: string; [key: string]: unknown };
        roomId?:
          | string
          | {
              _id?: string;
              id?: string;
              name?: string;
              description?: string;
              imageUrl?: string[];
            };
        roomOptionId?: string;
        roomData?: {
          _id?: string;
          id?: string;
          name?: string;
          description?: string;
          imageUrl?: string[];
        };
        transfer?: {
          _id: string;
          pickUpPoint: string;
          dropOffPoint: string;
          type: string;
          duration?: string;
          vehicleId?: string;
        };
      };
      const hotelId = it.hotelId?._id;

      const hotelForDay: Hotel[] = [];
      if (hotelId && hotels.length > 0) {
        const found = (hotels as HotelWithId[]).find(
          h =>
            (h as HotelWithId)._id === hotelId ||
            (h as HotelWithId).uid === hotelId
        );
        if (found) hotelForDay.push(found);
      }

      const transferBefore = transferByDay.before[index];
      const transferAfter = transferByDay.after[index];
      const vehicleIds = new Set<string>();
      if (transferBefore?.vehicleId) vehicleIds.add(transferBefore.vehicleId);
      if (transferAfter?.vehicleId) vehicleIds.add(transferAfter.vehicleId);
      const vehiclesForDay = Array.from(vehicleIds)
        .map(id => vehicleById[id])
        .filter(Boolean);

      const transfersForDay: TransferDisplayItem[] = [];
      const hotelsWithId = hotels as HotelWithId[];
      if (transferBefore?.vehicleId) {
        const vehicle = vehicleById[transferBefore.vehicleId];
        if (vehicle) {
          transfersForDay.push({
            fromName: resolvePointName(
              transferBefore.pickUpPoint,
              hotelsWithId,
              airports
            ),
            toName: resolvePointName(
              transferBefore.dropOffPoint,
              hotelsWithId,
              airports
            ),
            duration: transferBefore.duration,
            vehicle,
          });
        }
      }
      if (transferAfter?.vehicleId) {
        const vehicle = vehicleById[transferAfter.vehicleId];
        if (vehicle) {
          transfersForDay.push({
            fromName: resolvePointName(
              transferAfter.pickUpPoint,
              hotelsWithId,
              airports
            ),
            toName: resolvePointName(
              transferAfter.dropOffPoint,
              hotelsWithId,
              airports
            ),
            duration: transferAfter.duration,
            vehicle,
          });
        }
      }

      const hotelFromItinerary =
        it.hotelId &&
        typeof it.hotelId === 'object' &&
        (it.hotelId._id != null || it.hotelId.name != null)
          ? (it.hotelId as import('./ItineraryInfoPopover').HotelFromItinerary)
          : undefined;
      const hotelRoomTypes = parseRoomOptionsInput(
        (hotelFromItinerary as { roomTypes?: unknown[] } | undefined)?.roomTypes
      );

      const roomsFromItinerary = parseRoomOptionsInput(
        it.roomData ?? it.roomId
      );

      const roomIdentifierFromItinerary =
        (typeof it.roomId === 'string' &&
        it.roomId.trim() &&
        !it.roomId.trim().startsWith('[')
          ? it.roomId
          : '') ||
        it.roomOptionId ||
        '';

      let resolvedRoomOptions: NormalizedRoomOption[] = roomsFromItinerary.length
        ? roomsFromItinerary
        : hotelRoomTypes;

      if (roomIdentifierFromItinerary) {
        const selectedRoom = resolvedRoomOptions.find(
          room => room.id === roomIdentifierFromItinerary
        );
        if (selectedRoom) {
          resolvedRoomOptions = [selectedRoom];
        }
      }

      const roomFromItinerary =
        resolvedRoomOptions.length === 1
          ? {
              _id: resolvedRoomOptions[0].id,
              name: resolvedRoomOptions[0].name,
              description: resolvedRoomOptions[0].description,
              imageUrl: resolvedRoomOptions[0].imageUrl,
            }
          : resolvedRoomOptions.length > 1
            ? {
                _id: resolvedRoomOptions.map(room => room.id).join(','),
                name: formatRoomOptionsLabel(resolvedRoomOptions),
                description: '',
                imageUrl: resolvedRoomOptions.flatMap(room => room.imageUrl),
              }
            : undefined;
      const transferFromItinerary =
        it.transfer &&
        typeof it.transfer === 'object' &&
        (it.transfer._id != null || it.transfer.pickUpPoint != null)
          ? it.transfer
          : undefined;

      const finalTransfersForDay: TransferDisplayItem[] = transferFromItinerary
        ? [
            {
              fromName: resolvePointName(
                transferFromItinerary.pickUpPoint,
                hotelsWithId,
                airports
              ),
              toName: resolvePointName(
                transferFromItinerary.dropOffPoint,
                hotelsWithId,
                airports
              ),
              duration: transferFromItinerary.duration,
              vehicle: transferFromItinerary.vehicleId
                ? vehicleById[transferFromItinerary.vehicleId]
                : undefined,
            },
          ]
        : transfersForDay;

      return {
        ...itinerary,
        day: index + 1,
        hotelsForDay: hotelForDay,
        vehiclesForDay,
        transfersForDay: finalTransfersForDay,
        hotelFromItinerary,
        roomFromItinerary,
        roomOptionsFromItinerary: resolvedRoomOptions,
        transferFromItinerary,
      };
    });
  }, [sortedItineraries, hotels, airports, transferByDay, vehicleById]);

  const hasAccommodation = dayInfo.some(
    d =>
      d.hotelsForDay?.length > 0 ||
      Boolean(d.hotelFromItinerary) ||
      Boolean(d.roomFromItinerary) ||
      (d.roomOptionsFromItinerary?.length ?? 0) > 0
  );
  const hasTransfer = dayInfo.some(
    d => d.transfersForDay?.length > 0 || Boolean(d.transferFromItinerary)
  );

  return (
    <section className=''>
      <div className='mb-8'>
        <div className='flex flex-wrap items-center justify-between gap-3 mb-4'>
          <ActivityTitle title={'The Itinerary'} className='!mb-0' />
          <div
            className='flex items-center rounded-lg border border-[#D9D9D9] p-0.5'
            role='tablist'
            aria-label='Itinerary view'
          >
            <button
              type='button'
              onClick={() => setViewMode('list')}
              aria-pressed={viewMode === 'list'}
              aria-label='List view'
              className={`flex items-center justify-center w-9 h-9 rounded-md transition-colors ${
                viewMode === 'list'
                  ? 'bg-black text-white'
                  : 'bg-transparent text-[#7A7A7A] hover:bg-gray-100'
              }`}
            >
              <svg
                width='18'
                height='18'
                viewBox='0 0 18 18'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                aria-hidden
              >
                <path
                  d='M2 4h14M2 9h14M2 14h14'
                  stroke='currentColor'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                />
              </svg>
            </button>
            <button
              type='button'
              onClick={() => setViewMode('grid')}
              aria-pressed={viewMode === 'grid'}
              aria-label='Grid view'
              className={`flex items-center justify-center w-9 h-9 rounded-md transition-colors ${
                viewMode === 'grid'
                  ? 'bg-black text-white'
                  : 'bg-transparent text-[#7A7A7A] hover:bg-gray-100'
              }`}
            >
              <svg
                width='18'
                height='18'
                viewBox='0 0 18 18'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                aria-hidden
              >
                <rect
                  x='1'
                  y='1'
                  width='7'
                  height='7'
                  rx='1'
                  stroke='currentColor'
                  strokeWidth='1.5'
                />
                <rect
                  x='10'
                  y='1'
                  width='7'
                  height='7'
                  rx='1'
                  stroke='currentColor'
                  strokeWidth='1.5'
                />
                <rect
                  x='1'
                  y='10'
                  width='7'
                  height='7'
                  rx='1'
                  stroke='currentColor'
                  strokeWidth='1.5'
                />
                <rect
                  x='10'
                  y='10'
                  width='7'
                  height='7'
                  rx='1'
                  stroke='currentColor'
                  strokeWidth='1.5'
                />
              </svg>
            </button>
          </div>
        </div>

        {viewMode === 'list' ? (
          <div className='pl-12'>
            {dayInfo.map((day, index) => {
              const {
                hotelsForDay,
                vehiclesForDay,
                transfersForDay,
                hotelFromItinerary,
                roomFromItinerary,
                roomOptionsFromItinerary,
                transferFromItinerary,
                ...dayRest
              } = day;
              return (
                <ItineraryDay
                  key={day._id}
                  {...dayRest}
                  isLast={index === dayInfo.length - 1}
                  backendCurrency={backendCurrency}
                  hotels={hotelsForDay}
                  vehicles={vehiclesForDay}
                  transfersForDay={transfersForDay}
                  showItineraryInfoIcon={showItineraryInfoIcon}
                  showBenefits={showBenefits}
                  showHotelAsHighlights={showHotelAsHighlights}
                  hotelFromItinerary={hotelFromItinerary}
                  roomFromItinerary={roomFromItinerary}
                  roomOptionsFromItinerary={roomOptionsFromItinerary}
                  transferFromItinerary={transferFromItinerary}
                  isBookingContext={isBookingContext}
                />
              );
            })}
          </div>
        ) : (
          <ItineraryTableView
            dayInfo={dayInfo}
            startDate={startDate}
            hasAccommodation={hasAccommodation}
            hasTransfer={hasTransfer}
          />
        )}
      </div>
      <Border />
    </section>
  );
};
export default Itinerary;
